"use client";

/**
 * Status filter chip strip for the Enrollments tab (Phase J · Task 56).
 *
 * The list endpoint accepts a single `status=` query param (see
 * `ListEnrollmentsParams`), so this strip is single-select with an
 * "All" toggle. Clicking the active chip clears it.
 *
 * Order mirrors `ENROLLMENT_STATUS_ORDER` (active states first, terminal
 * states last). The chip palette echoes `ENROLLMENT_STATUS_META` so a
 * recruiter scanning the strip sees the same colour treatment as the
 * row pill.
 */

import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import {
  ENROLLMENT_STATUS_META,
  ENROLLMENT_STATUS_ORDER,
} from "../../lib/status-meta";
import type { EnrollmentStatus } from "../../lib/types";

interface StatusFilterStripProps {
  active: EnrollmentStatus | null;
  onChange: (next: EnrollmentStatus | null) => void;
}

export function StatusFilterStrip({
  active,
  onChange,
}: StatusFilterStripProps) {
  return (
    <div
      role="tablist"
      aria-label="Filter enrollments by status"
      className="flex flex-wrap items-center gap-1.5"
    >
      <FilterChip
        label="All"
        isActive={active === null}
        onClick={() => onChange(null)}
        testId="enrollment-filter-all"
      />
      {ENROLLMENT_STATUS_ORDER.map((status) => {
        const meta = ENROLLMENT_STATUS_META[status];
        const isActive = active === status;
        return (
          <FilterChip
            key={status}
            label={meta.label}
            isActive={isActive}
            onClick={() => onChange(isActive ? null : status)}
            iconColorClass={meta.color}
            iconName={meta.icon}
            testId={`enrollment-filter-${status}`}
          />
        );
      })}
    </div>
  );
}

interface FilterChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  iconColorClass?: string;
  iconName?: React.ComponentProps<typeof Icon>["name"];
  testId: string;
}

function FilterChip({
  label,
  isActive,
  onClick,
  iconColorClass,
  iconName,
  testId,
}: FilterChipProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-colors",
        isActive
          ? "bg-white/8 text-heading"
          : "bg-white/4 text-muted-foreground hover:bg-white/6 hover:text-foreground",
      )}
    >
      {iconName && (
        <Icon
          name={iconName}
          size={12}
          className={cn(iconColorClass, "shrink-0")}
        />
      )}
      <span>{label}</span>
    </button>
  );
}
